import { supabase } from '../config/supabase';
import { executeWithRetry } from '../config/supabase';

class AuthService {
  constructor() {
    // Prevent multiple instances
    if (AuthService.instance) {
      return AuthService.instance;
    }
    AuthService.instance = this;
    
    this.currentUser = null;
    this.isLoading = true;
    this.listeners = [];
    this.authSubscription = null;
    this.dbConnectionFailed = false; // Track if database connection is failing
    
    // Check session storage
    console.log('🔐 AuthService: Checking session storage...');
    console.log('🔐 localStorage available:', typeof localStorage !== 'undefined');
    console.log('🔐 sessionStorage available:', typeof sessionStorage !== 'undefined');
    
    // Check if we have any existing session data
    try {
      const supabaseSession = localStorage.getItem('sb-' + process.env.REACT_APP_SUPABASE_URL?.split('//')[1]?.split('.')[0] + '-auth-token');
      console.log('🔐 Existing Supabase session in localStorage:', !!supabaseSession);
      console.log('🔐 Session storage key:', 'sb-' + process.env.REACT_APP_SUPABASE_URL?.split('//')[1]?.split('.')[0] + '-auth-token');
      console.log('🔐 All localStorage keys:', Object.keys(localStorage).filter(key => key.includes('supabase') || key.includes('sb-')));
      
      if (supabaseSession) {
        try {
          const sessionData = JSON.parse(supabaseSession);
          console.log('🔐 Session data:', {
            hasAccessToken: !!sessionData.access_token,
            expiresAt: sessionData.expires_at,
            isExpired: sessionData.expires_at ? new Date(sessionData.expires_at * 1000) < new Date() : true
          });
          
          // Check if session is corrupted or expired
          if (!sessionData.access_token || (sessionData.expires_at && new Date(sessionData.expires_at * 1000) < new Date())) {
            console.warn('⚠️ Session appears corrupted or expired, clearing it');
            this.clearCorruptedSession();
          }
        } catch (parseError) {
          console.error('❌ Error parsing session data, clearing corrupted session:', parseError);
          this.clearCorruptedSession();
        }
      } else {
        // Check for other possible session storage keys
        const allKeys = Object.keys(localStorage);
        const supabaseKeys = allKeys.filter(key => key.includes('supabase') || key.includes('sb-'));
        console.log('🔐 All Supabase-related keys:', supabaseKeys);
        
        if (supabaseKeys.length > 0) {
          supabaseKeys.forEach(key => {
            try {
              const data = localStorage.getItem(key);
              console.log(`🔐 Key ${key}:`, data ? 'has data' : 'empty');
            } catch (e) {
              console.log(`🔐 Key ${key}: error reading - possibly corrupted`);
              // Remove corrupted keys
              try {
                localStorage.removeItem(key);
                console.log(`🔐 Removed corrupted key: ${key}`);
              } catch (removeError) {
                console.error(`❌ Failed to remove corrupted key ${key}:`, removeError);
              }
            }
          });
        }
      }
    } catch (error) {
      console.error('❌ Error checking session storage:', error);
      this.clearCorruptedSession();
    }
    
    // Initialize auth state listener
    this.initializeAuth();
  }

  initializeAuth() {
    console.log('🔐 AuthService: Initializing auth...');
    
    // Add timeout fallback to prevent infinite loading
    const authTimeout = setTimeout(() => {
      console.warn('⚠️ Auth initialization timeout - forcing loading to false');
      if (this.isLoading) {
        this.isLoading = false;
        this.currentUser = null;
        this.notifyListeners();
      }
    }, 10000); // 10 second timeout
    
    // Clear timeout when auth resolves
    const clearAuthTimeout = () => {
      if (authTimeout) {
        clearTimeout(authTimeout);
      }
    };
    
    // Listen to auth state changes
    this.authSubscription = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('🔄 Supabase auth state changed:', event, session?.user?.email || 'No user');
      console.log('🔄 Session details:', {
        event,
        hasUser: !!session?.user,
        email: session?.user?.email,
        emailConfirmed: !!session?.user?.email_confirmed_at,
        sessionExpiresAt: session?.expires_at
      });
      
      // Clear the auth timeout since we got a response
      clearAuthTimeout();
      
      if (event === 'SIGNED_IN' && session?.user) {
        // Only load profile if email is confirmed
        if (session.user.email_confirmed_at) {
          await this.loadUserProfile(session.user);
        } else {
          console.log('⚠️ User signed in but email not confirmed, skipping profile load');
          this.currentUser = null;
          this.isLoading = false;
          this.notifyListeners();
        }
      } else if (event === 'SIGNED_OUT') {
        console.log('🔐 User signed out, clearing current user');
        this.currentUser = null;
        this.isLoading = false;
        this.notifyListeners();
      } else if (event === 'TOKEN_REFRESHED' && session?.user) {
        console.log('🔄 Token refreshed, checking if we need to load profile');
        // Only refresh if email is confirmed and we don't already have a user
        if (session.user.email_confirmed_at && !this.currentUser) {
          console.log('🔄 Loading profile after token refresh');
          await this.loadUserProfile(session.user);
        } else {
          // Token refreshed but no action needed
          this.isLoading = false;
          this.notifyListeners();
        }
      } else if (event === 'INITIAL_SESSION' && session?.user) {
        console.log('🔄 Initial session found, loading profile');
        if (session.user.email_confirmed_at) {
          await this.loadUserProfile(session.user);
        } else {
          console.log('⚠️ Initial session user email not confirmed');
          this.currentUser = null;
          this.isLoading = false;
          this.notifyListeners();
        }
      } else if (event === 'INITIAL_SESSION' && !session?.user) {
        console.log('🔄 No initial session found');
        this.currentUser = null;
        this.isLoading = false;
        this.notifyListeners();
      } else {
        // Handle any other auth events
        console.log('🔄 Other auth event, ensuring loading is false');
        this.isLoading = false;
        this.notifyListeners();
      }
    });
  }

  async loadUserProfile(authUser) {
    try {
      console.log('🔍 Loading user profile for:', authUser.email);
      
      // Check if email is verified
      if (!authUser.email_confirmed_at) {
        console.log('⚠️ Email not verified for user:', authUser.email);
        this.currentUser = null;
        this.isLoading = false;
        this.notifyListeners();
        return;
      }

      // Load metadata first for instant UI, then enhance with database data
      console.log('🔄 Using metadata-based profile for instant load');
      this.loadUserProfileFromMetadata(authUser);
      
      // Background enhancement: Load full profile data after initial load
      setTimeout(() => {
        this.enhanceProfileFromDatabase(authUser);
      }, 1000); // 1 second delay for smooth UX
      
    } catch (error) {
      console.error('❌ Error loading user profile:', error);
      this.loadUserProfileFromMetadata(authUser);
    }
  }

  loadUserProfileFromMetadata(authUser) {
    console.log('📊 Using auth user metadata for instant load');
    
    const metadata = authUser.user_metadata || {};
    
    // Create user object from auth data
    this.currentUser = {
      id: authUser.id,
      email: authUser.email,
      username: metadata.username || authUser.email.split('@')[0],
      user_type: metadata.user_type || metadata.type || 'fan',
      user_level: 1, // Default level - will be enhanced from DB
      type: metadata.user_type || metadata.type || 'fan', // Keep for backward compatibility
      role: metadata.user_type || metadata.type || 'fan', // Keep for backward compatibility
      emailVerified: true,
      profileCompleted: true,
      scenePoints: 0, // Default - will be enhanced from DB
      scene_points: 0, // Default - will be enhanced from DB
      isAdvertiser: false, // Default - will be enhanced from DB
      isAdmin: false, // Default - will be enhanced from DB
      profile: {
        bio: metadata.bio || '',
        location: metadata.city && metadata.state ? `${metadata.city}, ${metadata.state}` : 'Unknown Location',
        city: metadata.city || '',
        state: metadata.state || '',
        joinDate: authUser.created_at
      },
      createdAt: authUser.created_at,
      isEnhanced: false // Flag to track if we've loaded full DB data
    };
    
    console.log('✅ User profile loaded from auth metadata:', this.currentUser.username, 'Type:', this.currentUser.user_type);
    console.log('🔍 Notifying listeners with instant profile...');
    this.isLoading = false;
    this.notifyListeners();
    console.log('🔍 Instant profile load completed');
  }

  async enhanceProfileFromDatabase(authUser) {
    try {
      console.log('🔄 Background: Enhancing profile with database data...');
      
      // Quick database query with short timeout for mobile
      const enhanceTimeout = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Enhancement timeout')), 5000); // 5s timeout
      });
      
      const dbQuery = supabase
        .from('users')
        .select('*')
        .eq('id', authUser.id)
        .single();
      
      const { data: userProfileData, error } = await Promise.race([dbQuery, enhanceTimeout]);
      
      if (error) {
        console.log('⚠️ Background enhancement failed (not critical):', error.message);
        return; // Fail silently - user already has working profile
      }
      
      if (userProfileData && this.currentUser && !this.currentUser.isEnhanced) {
        console.log('✅ Background: Enhancing profile with database data');
        
        // Enhance existing profile with database data
        this.currentUser = {
          ...this.currentUser,
          username: userProfileData.username || this.currentUser.username,
          user_type: userProfileData.user_type || this.currentUser.user_type,
          user_level: userProfileData.user_level || this.currentUser.user_level,
          scene_points: userProfileData.scene_points || this.currentUser.scene_points,
          scenePoints: userProfileData.scene_points || this.currentUser.scenePoints,
          type: userProfileData.user_type || this.currentUser.type,
          role: userProfileData.user_type || this.currentUser.role,
          isAdvertiser: userProfileData.is_approved_advertiser || false,
          isAdmin: userProfileData.is_admin || false,
          profile: {
            ...this.currentUser.profile,
            bio: userProfileData.bio || this.currentUser.profile.bio,
            location: userProfileData.city && userProfileData.state ? 
              `${userProfileData.city}, ${userProfileData.state}` : 
              this.currentUser.profile.location,
            city: userProfileData.city || this.currentUser.profile.city,
            state: userProfileData.state || this.currentUser.profile.state,
            joinDate: userProfileData.created_at || this.currentUser.profile.joinDate
          },
          isEnhanced: true
        };
        
        console.log('✅ Profile enhanced:', this.currentUser.username, 'Level:', this.currentUser.user_level, 'Points:', this.currentUser.scene_points);
        
        // Notify listeners of the enhanced profile
        this.notifyListeners();
      }
      
    } catch (error) {
      console.log('⚠️ Background profile enhancement failed (not critical):', error.message);
      // Fail silently - user already has a working profile from metadata
    }
  }

  async createUserProfileFromAuth(authUser) {
    try {
      console.log('🔍 Creating user profile from auth data...');
      
      const profileData = {
        id: authUser.id,
        email: authUser.email,
        username: authUser.user_metadata?.username || authUser.email.split('@')[0],
        user_type: authUser.user_metadata?.user_type || 'fan',
        user_level: 1,
        scene_points: 0,
        city: authUser.user_metadata?.city || '',
        state: authUser.user_metadata?.state || '',
        is_approved_advertiser: false,
        is_admin: false,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      console.log('🔍 Inserting user profile:', profileData);
      
      // Use enhanced retry logic for profile creation
      const { error } = await executeWithRetry(async () => {
        return await supabase
          .from('users')
          .insert([profileData]);
      }, 3, 2000);

      if (error) {
        console.error('❌ Error creating user profile:', error);
        throw error;
      }

      console.log('✅ User profile created successfully');
    } catch (error) {
      console.error('❌ Failed to create user profile:', error);
      throw error;
    }
  }

  async register({ email, password, username, user_type, city, state }) {
    try {
      console.log('🔐 Starting registration process for:', email);
      
      this.isLoading = true;
      this.notifyListeners();

      console.log('🔐 Calling Supabase signUp...');
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            username,
            user_type: user_type && user_type.toLowerCase(),
            city,
            state
          },
          emailRedirectTo: `${window.location.origin}/auth/callback`,
          shouldCreateUser: true
        }
      });

      if (error) {
        console.error('❌ Registration error:', error);
        console.error('❌ Error details:', {
          message: error.message,
          status: error.status,
          name: error.name
        });
        let message = 'Registration failed';
        
        switch (error.message) {
          case 'User already registered':
            message = 'An account with this email already exists';
            break;
          case 'Password should be at least 6 characters':
            message = 'Password must be at least 6 characters long';
            break;
          case 'Invalid email':
            message = 'Please enter a valid email address';
            break;
          default:
            message = error.message || 'Registration failed';
        }
        
        return { success: false, error: message };
      }

      if (data.user) {
        console.log('✅ User created in auth, confirmation email sent');
        console.log('📝 User data:', data.user);
        return { 
          success: true, 
          user: null, // No user object until email confirmed
          message: 'Account created! Please check your email to confirm your account before signing in.',
          needsEmailConfirmation: true
        };
      }

      return { success: false, error: 'Registration failed' };
    } catch (error) {
      console.error('💥 Registration error:', error);
      return { success: false, error: error.message || 'Registration failed' };
    } finally {
      this.isLoading = false;
      this.notifyListeners();
    }
  }

  async login(email, password) {
    try {
      console.log('🔐 Starting login process for:', email);
      
      this.isLoading = true;
      this.notifyListeners();

      console.log('🔐 Calling Supabase signInWithPassword...');
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });
      console.log('🔐 Supabase signInWithPassword completed');

      if (error) {
        console.error('🔐 Login error:', error);
        
        let message = 'Login failed';
        
        switch (error.message) {
          case 'Invalid login credentials':
            message = 'Invalid email or password';
            break;
          case 'Email not confirmed':
            message = 'Please check your email and confirm your account before signing in';
            break;
          case 'Too many requests':
            message = 'Too many login attempts. Please try again later.';
            break;
          default:
            message = error.message || 'Login failed';
        }
        
        return { success: false, error: message };
      }

      if (data.user) {
        console.log('🔐 User data received from Supabase');
        // Check if email is confirmed
        if (!data.user.email_confirmed_at) {
          console.log('🔐 Email not confirmed, signing out');
          await supabase.auth.signOut(); // Sign out unconfirmed user
          return { 
            success: false, 
            error: 'Please confirm your email address before signing in. Check your inbox for a confirmation link.',
            needsEmailConfirmation: true
          };
        }

        console.log('🔐 User authenticated, loading profile...');
        
        // Load user profile with timeout to prevent hanging
        try {
          const profileTimeoutPromise = new Promise((_, reject) => {
            setTimeout(() => reject(new Error('Profile loading timeout')), 15000); // 15 second timeout
          });
          
          const profilePromise = this.loadUserProfile(data.user);
          
          await Promise.race([profilePromise, profileTimeoutPromise]);
          console.log('🔐 User profile loaded successfully');
        } catch (profileError) {
          console.error('❌ Profile loading failed:', profileError);
          // Fallback to metadata if profile loading fails
          console.log('🔄 Falling back to metadata due to profile loading failure');
          this.loadUserProfileFromMetadata(data.user);
        }
        
        // Debug session storage after login
        try {
          const supabaseSession = localStorage.getItem('sb-' + process.env.REACT_APP_SUPABASE_URL?.split('//')[1]?.split('.')[0] + '-auth-token');
          console.log('🔐 Session after login:', !!supabaseSession);
          if (supabaseSession) {
            const sessionData = JSON.parse(supabaseSession);
            console.log('🔐 Session data after login:', {
              hasAccessToken: !!sessionData.access_token,
              expiresAt: sessionData.expires_at,
              isExpired: sessionData.expires_at ? new Date(sessionData.expires_at * 1000) < new Date() : true
            });
          }
        } catch (error) {
          console.error('❌ Error checking session after login:', error);
        }
        
        if (!this.currentUser) {
          console.log('⚠️ No current user after profile loading, using fallback');
          // Try one more time with metadata fallback
          this.loadUserProfileFromMetadata(data.user);
        }
        
        if (!this.currentUser) {
          return { success: false, error: 'Failed to load user profile. Please contact support if this persists.' };
        }

        console.log('🔐 Login process completed successfully');
        return { success: true, user: this.currentUser };
      }

      return { success: false, error: 'Login failed' };
    } catch (error) {
      console.error('💥 Login error:', error);
      return { success: false, error: error.message || 'Login failed' };
    } finally {
      console.log('🔐 Login process finished, setting loading to false');
      this.isLoading = false;
      this.notifyListeners();
    }
  }

  async logout() {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) {
        console.error('Logout error:', error);
        return { success: false, error: error.message };
      }
      
      this.currentUser = null;
      this.notifyListeners();
      return { success: true };
    } catch (error) {
      console.error('Logout error:', error);
      return { success: false, error: error.message };
    }
  }

  async resetPassword(email) {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `https://scenefeed.live/auth/reset-password`
      });

      if (error) {
        return { success: false, error: error.message };
      }

      return { 
        success: true, 
        message: 'Password reset email sent! Check your inbox.' 
      };
    } catch (error) {
      console.error('Password reset error:', error);
      return { success: false, error: error.message };
    }
  }

  async resendConfirmation(email) {
    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/callback`
        }
      });

      if (error) {
        return { success: false, error: error.message };
      }

      return { 
        success: true, 
        message: 'Confirmation email sent! Check your inbox.' 
      };
    } catch (error) {
      console.error('Resend confirmation error:', error);
      return { success: false, error: error.message };
    }
  }

  async updateProfile(updates) {
    if (!this.currentUser) {
      return { success: false, error: 'Not logged in' };
    }

    try {
      // Update in our users table
      const { error } = await supabase
        .from('users')
        .update({
          username: updates.username,
          city: updates.city,
          state: updates.state,
          updated_at: new Date().toISOString()
        })
        .eq('id', this.currentUser.id);

      if (error) {
        console.error('Error updating profile:', error);
        return { success: false, error: error.message };
      }

      // Reload user profile
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        await this.loadUserProfile(user);
      }

      return { success: true };
    } catch (error) {
      console.error('Error updating profile:', error);
      return { success: false, error: error.message };
    }
  }

  async updateUserRole(userId, newRole) {
    if (!this.isAdmin) {
      return { success: false, error: 'Only admins can update user roles' };
    }

    try {
      // Validate the role value
      const validRoles = ['fan', 'band', 'venue'];
      if (!validRoles.includes(newRole)) {
        return { success: false, error: 'Invalid role. Must be fan, band, or venue.' };
      }

      const { error } = await supabase
        .from('users')
        .update({ user_type: newRole })
        .eq('id', userId);

      if (error) {
        console.error('Error updating user role:', error);
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (error) {
      console.error('Error updating user role:', error);
      return { success: false, error: error.message };
    }
  }

  async getAllUsers() {
    if (!this.isAdmin) {
      return { success: false, error: 'Only admins can view all users' };
    }

    try {
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching users:', error);
        return { success: false, error: error.message };
      }

      return { success: true, users: data };
    } catch (error) {
      console.error('Error fetching users:', error);
      return { success: false, error: error.message };
    }
  }

  async updateScenePoints(points) {
    try {
      if (!this.currentUser) {
        throw new Error('No user logged in');
      }

      const { data, error } = await supabase
        .from('users')
        .update({ 
          scene_points: this.currentUser.scenePoints + points 
        })
        .eq('id', this.currentUser.id)
        .select('scene_points')
        .single();

      if (error) {
        throw error;
      }

      // Update local user state
      this.currentUser.scenePoints = data.scene_points;
      this.notifyListeners();

      return { 
        success: true, 
        newTotal: data.scene_points 
      };

    } catch (error) {
      console.error('Error updating scene points:', error);
      return { 
        success: false, 
        error: error.message 
      };
    }
  }

  // Role checking methods
  get isLoggedIn() {
    return !!this.currentUser && !!this.currentUser.emailVerified;
  }

  get isAdmin() {
    return this.currentUser?.isAdmin === true;
  }

  get isAdvertiser() {
    return this.currentUser?.isAdvertiser === true || this.isAdmin;
  }

  get canPost() {
    return this.isLoggedIn;
  }

  get canModerate() {
    return this.isAdmin;
  }

  get canManageAds() {
    return this.isAdvertiser;
  }

  // Event listeners
  addListener(callback) {
    this.listeners.push(callback);
  }

  removeListener(callback) {
    this.listeners = this.listeners.filter(listener => listener !== callback);
  }

  notifyListeners() {
    console.log('🔐 AuthService: Notifying listeners, count:', this.listeners.length);
    this.listeners.forEach((callback, index) => {
      try {
        console.log(`🔐 AuthService: Calling listener ${index} with user:`, this.currentUser?.email || 'null', 'loading:', this.isLoading);
        callback(this.currentUser, this.isLoading);
      } catch (error) {
        console.error('Error in auth listener:', error);
      }
    });
  }

  clearCorruptedSession() {
    console.log('🧹 Clearing corrupted session data...');
    try {
      // Clear all Supabase-related localStorage items
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (key.includes('supabase') || key.includes('sb-'))) {
          keysToRemove.push(key);
        }
      }
      
      keysToRemove.forEach(key => {
        try {
          localStorage.removeItem(key);
          console.log(`🧹 Removed corrupted session key: ${key}`);
        } catch (error) {
          console.error(`❌ Failed to remove key ${key}:`, error);
        }
      });
      
      // Force sign out to clear any remaining auth state
      supabase.auth.signOut().catch(error => {
        console.error('❌ Error during forced sign out:', error);
      });
      
    } catch (error) {
      console.error('❌ Error clearing corrupted session:', error);
    }
  }

  destroy() {
    if (this.authSubscription) {
      this.authSubscription.data.subscription.unsubscribe();
    }
    this.listeners = [];
    this.currentUser = null;
  }
}

// Create singleton instance
const authService = new AuthService();

// Add global recovery method for debugging
if (typeof window !== 'undefined') {
  window.recoverAuth = () => {
    console.log('🔧 Manual auth recovery triggered');
    authService.clearCorruptedSession();
    authService.isLoading = false;
    authService.currentUser = null;
    authService.notifyListeners();
    console.log('🔧 Auth recovery completed - try refreshing the page');
  };
}

export default authService; 